import { Link } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";

/**
 * Page « À propos ».
 * Le centre de gravité reste Aoria. Aucune photo du fondateur n'est
 * affichée — l'initiative se présente comme durable, indépendante de
 * la seule personne qui l'a portée à son origine.
 */
export default function APropos() {
    return (
        <PageLayout testid="about-page">
            <Seo
                title="À propos d'Aoria · Une initiative consacrée à l'accessibilité cognitive"
                description="À l'origine d'Aoria : une association consacrée à l'accessibilité cognitive à l'ère numérique. Approche pragmatique, sobre et fondée sur des usages concrets."
                path="/a-propos"
            />
            <PageHeader
                testid="a-propos"
                eyebrow="À PROPOS"
                title="À l'origine d'Aoria,"
                italic="une initiative consacrée à l'accessibilité cognitive"
                lead="Une approche pragmatique, sobre et fondée sur des usages concrets : moins de discours abstraits, davantage d'attention à ce que les outils numériques permettent réellement lorsqu'ils sont utilisés avec discernement."
                backdrop="dots"
            />

            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-3">
                        <span className="editorial-num text-lg">01</span>
                        <div className="hairline mt-3 pb-3" />
                        <p className="eyebrow mt-4">L'origine</p>
                    </div>
                    <div className="lg:col-span-9 max-w-3xl">
                        <h2 className="h2 text-2xl md:text-3xl lg:text-[2.25rem] text-[#0D1B2A] leading-snug">
                            Une initiative portée par Simon Marie.
                        </h2>
                        <div className="mt-8 space-y-6 text-[#1B263B] text-base md:text-lg leading-relaxed">
                            <p>
                                <strong className="font-medium">
                                    Simon Marie
                                </strong>{" "}
                                est à l'origine d'Aoria.
                            </p>
                            <p>
                                Son parcours personnel, familial et intellectuel
                                l'a conduit à s'intéresser aux obstacles
                                cognitifs invisibles, aux difficultés d'accès
                                aux codes ordinaires et aux outils qui peuvent
                                renforcer l'autonomie des personnes dans leur
                                vie quotidienne, leurs études ou leur travail.
                            </p>
                            <p>
                                Avec Aoria, il porte une approche pragmatique de
                                l'accessibilité cognitive : moins de discours
                                abstraits, davantage d'usages concrets, et une
                                attention particulière à ce que les outils
                                numériques peuvent réellement permettre
                                lorsqu'ils sont utilisés avec discernement.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section 02 */}
                <div className="mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-3">
                        <span className="editorial-num text-lg">02</span>
                        <div className="hairline mt-3 pb-3" />
                        <p className="eyebrow mt-4">L'organisation</p>
                    </div>
                    <div className="lg:col-span-9 max-w-3xl">
                        <h2 className="h2 text-2xl md:text-3xl lg:text-[2.25rem] text-[#0D1B2A] leading-snug">
                            Qu'est-ce qu'Aoria ?
                        </h2>
                        <div className="mt-8 space-y-6 text-[#1B263B] text-base md:text-lg leading-relaxed">
                            <p>
                                Aoria est une association consacrée à
                                l'accessibilité cognitive à l'ère numérique.
                            </p>
                            <p>
                                Elle explore la manière dont les outils
                                numériques, dont l'intelligence artificielle,
                                peuvent aider à réduire certains obstacles
                                cognitifs dans les études, le travail, les
                                démarches administratives et la vie quotidienne.
                            </p>
                            <p>
                                Son approche est volontairement sobre :
                                comprendre les difficultés réelles, identifier
                                les usages utiles, distinguer l'aide de la
                                substitution, et défendre une idée simple -
                                l'équité suppose parfois des outils différents.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section 03 */}
                <div className="mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-3">
                        <span className="editorial-num text-lg">03</span>
                        <div className="hairline mt-3 pb-3" />
                        <p className="eyebrow mt-4">Le nom</p>
                    </div>
                    <div className="lg:col-span-9 max-w-3xl">
                        <h2 className="h2 text-2xl md:text-3xl lg:text-[2.25rem] text-[#0D1B2A] leading-snug">
                            Pourquoi Aoria ?
                        </h2>
                        <div className="mt-8 space-y-6 text-[#1B263B] text-base md:text-lg leading-relaxed">
                            <p>
                                AORIA signifie :{" "}
                                <span className="font-serif italic text-[#0D1B2A]">
                                    «{"\u00A0"}Accessibilité, Outils et
                                    Ressources par l'Intelligence
                                    Artificielle.{"\u00A0"}»
                                </span>
                            </p>
                            <p>
                                L'intelligence artificielle peut contribuer à
                                rendre l'information plus accessible, à
                                faciliter certaines démarches et à réduire
                                certains obstacles cognitifs dans les études,
                                le travail et la vie quotidienne.
                            </p>
                            <p>
                                Pour Aoria, l'intelligence artificielle n'est
                                pas une finalité. Elle constitue l'un des
                                outils susceptibles de favoriser l'autonomie,
                                l'accès aux ressources et une meilleure
                                accessibilité cognitive lorsqu'elle est
                                utilisée avec discernement.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Le projet Aoria, chronologie */}
                <section
                    data-testid="about-chronologie"
                    className="mt-24 md:mt-32 border-t border-[#E0E1DD] pt-16 md:pt-20"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12">
                        <div className="lg:col-span-3">
                            <div className="flex items-center gap-4">
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                                />
                                <span className="eyebrow">Le projet Aoria</span>
                            </div>
                        </div>
                        <div className="lg:col-span-9 max-w-3xl">
                            <h2 className="h2 text-2xl md:text-3xl lg:text-[2.25rem] text-[#0D1B2A] leading-snug">
                                Une trajectoire,{" "}
                                <span className="italic text-[#415A77]">
                                    en construction
                                </span>
                                .
                            </h2>
                        </div>
                    </div>

                    <ol
                        data-testid="about-chronologie-list"
                        className="border-l border-[#0D1B2A]/20 pl-8 md:pl-12 space-y-10 max-w-3xl ml-0 lg:ml-[25%]"
                    >
                        {[
                            {
                                phase: "2025",
                                title: "Constat initial",
                                body: "Un parcours personnel et professionnel met en évidence l'angle mort de l'accessibilité cognitive dans la transformation numérique.",
                            },
                            {
                                phase: "Mars 2026",
                                title: "Création de l'association",
                                body: "Aoria est déclarée comme association loi 1901, avec une mission claire : penser l'accessibilité cognitive à l'ère de l'intelligence artificielle.",
                            },
                            {
                                phase: "Mai 2026",
                                title: "Mise en ligne du site",
                                body: "Première version publique du site, ouverte aux institutions, aux entreprises, aux salariés, aux indépendants et aux acteurs de l'enseignement.",
                            },
                            {
                                phase: "2026, en cours",
                                title: "Premières ressources",
                                body: "Publication progressive d'articles, de guides et de fiches pratiques, sous comité éditorial sobre.",
                            },
                            {
                                phase: "À venir",
                                title: "Publication du livre blanc",
                                body: "Document de référence consacré aux enjeux de l'accessibilité cognitive à l'ère numérique, en libre accès.",
                            },
                            {
                                phase: "À venir",
                                title: "Développement des partenariats",
                                body: "Coopérations institutionnelles, scientifiques, associatives et industrielles, formalisées au rythme des conventionnements.",
                            },
                        ].map((step, i) => (
                            <li
                                key={step.title}
                                data-testid={`about-chronologie-step-${i}`}
                                className="relative"
                            >
                                <span
                                    aria-hidden="true"
                                    className="absolute -left-[calc(2rem+5px)] md:-left-[calc(3rem+5px)] top-[0.65rem] inline-block h-2 w-2 rounded-full bg-[#0D1B2A]"
                                />
                                <div className="eyebrow">{step.phase}</div>
                                <h3 className="h3 mt-3 text-xl md:text-2xl text-[#0D1B2A]">
                                    {step.title}
                                </h3>
                                <p className="mt-3 text-[#5C6B7D] leading-relaxed">
                                    {step.body}
                                </p>
                            </li>
                        ))}
                    </ol>
                </section>

                {/* Identity card */}
                <aside
                    data-testid="about-identity-card"
                    className="mt-24 md:mt-32 border-t border-[#E0E1DD] pt-12"
                >
                    <p className="eyebrow">Identité institutionnelle</p>
                    <dl className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4 text-sm">
                        <div>
                            <dt className="eyebrow">Forme</dt>
                            <dd className="mt-2 text-[#1B263B]">
                                Association loi 1901
                            </dd>
                        </div>
                        <div>
                            <dt className="eyebrow">SIREN</dt>
                            <dd className="mt-2 text-[#1B263B] font-mono tracking-wider">
                                993 013 549
                            </dd>
                        </div>
                        <div>
                            <dt className="eyebrow">Contact</dt>
                            <dd className="mt-2 text-[#1B263B]">
                                <Link
                                    to="/contact"
                                    className="link-underline"
                                >
                                    contact@aoria.org
                                </Link>
                            </dd>
                        </div>
                    </dl>
                    <p className="mt-10 text-xs tracking-[0.16em] uppercase text-[#5C6B7D]">
                        Aoria, Pour l'accessibilité cognitive à l'ère de
                        l'intelligence artificielle.
                    </p>
                </aside>
            </section>
        </PageLayout>
    );
}
